import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Calendar, MapPin, Filter } from 'lucide-react';
import axios from 'axios';
import EventCard from '../components/EventCard';
import { API } from '../lib/api';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        const liveEvents = Array.isArray(response.data)
          ? [...response.data].sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
          : [];
        setEvents(liveEvents);
        setFilteredEvents(liveEvents);
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
        setFilteredEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    let filtered = events;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.venue_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by city
    if (selectedCity !== 'all') {
      filtered = filtered.filter(event => event.city === selectedCity);
    }

    setFilteredEvents(filtered);
  }, [searchTerm, selectedCity, events]);

  const cities = ['all', ...new Set(events.map(e => e.city))];

  return (
    <div data-testid="events-page" className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="py-16 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-purple-500 font-semibold mb-2">Discover</p>
            <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">
              Upcoming Events
            </h1>
            <p className="text-zinc-400 text-lg max-w-2xl">
              Find the best events happening in Baltimore and beyond. From live music to art shows, 
              there's always something happening.
            </p>
          </motion.div>

          {/* Search and Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-8 flex flex-col sm:flex-row gap-4"
          >
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
              <input
                type="text"
                placeholder="Search events, venues..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="event-search-input"
                className="w-full bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-500 pl-12 pr-4 py-3 rounded-xl focus:outline-none focus:border-purple-500 transition-colors"
              />
            </div>

            {/* City Filter */}
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                data-testid="city-filter"
                className="appearance-none bg-zinc-900 border border-zinc-800 text-white pl-12 pr-10 py-3 rounded-xl focus:outline-none focus:border-purple-500 transition-colors cursor-pointer"
              >
                <option value="all">All Cities</option>
                {cities.filter(c => c !== 'all').map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
              <Filter className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" size={16} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="bg-zinc-900 rounded-xl overflow-hidden animate-pulse">
                  <div className="h-56 bg-zinc-800" />
                  <div className="p-6 space-y-3">
                    <div className="h-6 bg-zinc-800 rounded w-3/4" />
                    <div className="h-4 bg-zinc-800 rounded w-1/2" />
                    <div className="h-4 bg-zinc-800 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">{events.length === 0 ? 'No upcoming events right now' : 'No events found'}</h3>
              <p className="text-zinc-500">{events.length === 0 ? 'Check back soon for new Baltimore events.' : 'Try adjusting your search or filters'}</p>
            </div>
          ) : (
            <>
              <p className="text-zinc-500 mb-8">{filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredEvents.map((event, index) => (
                  <EventCard key={event.id} event={event} index={index} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Submit Event CTA */}
      <section className="py-16 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 text-center">
          <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-white mb-4">
            Have an event to share?
          </h2>
          <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
            Submit your event to be featured on What's Happening Baltimore and reach thousands of local event-goers.
          </p>
          <a
            href="https://events.sanitylounge.com/selection-page"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="submit-event-cta"
            className="inline-block bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300"
          >
            Submit Your Event
          </a>
        </div>
      </section>
    </div>
  );
};

export default Events;
